import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import type { IntlShape } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import { AiSuggestionsRegenerationTriggerValue } from '@atlaskit/editor-common/analytics/ai-suggestions-regeneration';
import { usePluginStateEffect } from '@atlaskit/editor-common/use-plugin-state-effect';
import type { EditorInjectionAPI } from '@atlaskit/editor-common/types/next-editor-plugin';
import { useSharedPluginStateSelector } from '@atlaskit/editor-common/useSharedPluginStateSelector';
import type { Provider } from '@atlaskit/collab-provider/provider';
import type { ProviderError, CollabEditProvider } from '@atlaskit/editor-common/collab';
import type { InlineCommentPluginState } from '@atlaskit/editor-plugin-annotation/pm-plugins/types';

import type { FullPageEditorPresetAPI } from '@confluence/editor-presets/entry-points/useFullPageEditorPreset.ts';
import { useObjectSidebarActions } from '@confluence/object-sidebar-api/entry-points/useObjectSidebar.ts';
import type { ReadPageContent } from '@confluence/embedded-confluence-allowed-features/entry-points/usePageNativeEmbedClient.ts';
import { useRegisterEditorReadPageContent } from '@confluence/embedded-confluence-allowed-features/entry-points/useRegisterEditorReadPageContent.ts';
import { useConnecting } from '@confluence/native-collab/entry-points/useConnecting.ts';
import { useSchedulePublishFlagsWatcher } from '@confluence/editor-schedule-publish/entry-points/useSchedulePublishFlagsWatcher.ts';
import { useEditorInlineCommentsPubSub } from '@confluence/editor-features/entry-points/useEditorInlineCommentsPubSub.ts';
import {
	useQuickReloadSubscriber,
	ReloadType,
	ReloadPriority,
} from '@confluence/quick-reload/entry-points/subscription.ts';
import { useSuggestedEditsUpdatedSubscription } from '@confluence/editor-features/entry-points/useSuggestedEditsUpdatedSubscription.ts';
import { useUnreadCommentsSetup } from '@confluence/unread-comments/entry-points/useUnreadCommentsSetup.ts';
import { useEditorAIReplacingTitle } from '@confluence/editor-features/entry-points/useEditorAIReplacingTitle.ts';
import { useSendContentAgeAnalytics } from '@confluence/editor-features/entry-points/useSendContentAgeAnalytics.ts';
import { useDisabledAnalytics } from '@confluence/editor-features/entry-points/useDisabledAnalytics.ts';
import { useScrollOnUrlChange } from '@confluence/editor-features/entry-points/useScrollOnUrlChange.ts';
import { useSpaceKeyShortcutOverride } from '@confluence/blank-page-quick-actions/entry-points/useSpaceKeyShortcutOverride.ts';
import { useExposeGoogleMeetScreenShare } from '@confluence/embedded-google-meet/entry-points/useExposeGoogleMeetScreenShare.ts';
import { usePrintCoverInjector } from '@confluence/pdf-print-utils/entry-points/coverInjector.ts';
import { useCommentsDataActions } from '@confluence/comments-data/entry-points/useCommentsData.ts';
import { useProcessedCommentsDataActions } from '@confluence/comments-data-manager/entry-points/useProcessedCommentsData.ts';
import { resetMediaViewerSidebar } from '@confluence/editor-features/entry-points/resetMediaViewerSidebar.ts';
import { useMentionsNotificationsActions } from '@confluence/mentions-notifications/entry-points/useMentionsNotifications.ts';
import { useInlineCommentsDispatchContext } from '@confluence/comment-context/entry-points/InlineCommentsContext.ts';
import { useCommentsPanelActions } from '@confluence/comments-panel-utils/entry-points/useCommentsPanel.ts';
import {
	expValEquals,
	expVal,
	UNSAFE_expValEqualsNoExposure,
} from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';
import { type AddCommentInCacheProps } from '@confluence/comments-data-manager/entry-points/useAllCommentsData.ts';
import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';
type TriggerAnalyticsEventType = (
	eventName: string,
	extraData?: { [key: string]: any },
) => Promise<void>;
import type { CollabHeartbeatError } from '@confluence/native-collab/entry-points/useEditorHeartbeat.ts';
import { useShowSuggestionsPanelFromDeepLink } from '@confluence/suggestions-panel/entry-points/useShowSuggestionsPanelFromDeepLink.ts';

import { useAgentMentionBridge } from './useAgentMentionBridge.ts';
import { useAgentRunStateBridge } from './useAgentRunStateBridge.ts';
import { useFullPageAutoFocus } from './useFullPageAutoFocus.ts';
import { useFocusedComment } from './useFocusedComment.ts';
import { useEditorCollabConnectivitySync } from './useEditorCollabConnectivitySync.ts';
import { useFullPageEditorLoomInsertion } from './useFullPageEditorLoomInsertion.ts';
import { useOpenRovoOnEditPageReady } from './useOpenRovoOnEditPageReady.ts';
import { useEmbeddedConfluenceTextSelectionBridge } from './useEmbeddedConfluenceTextSelectionBridge.ts';
import { LiveDocsFlagToShareSideEffect } from './LiveDocsFlagToShareIntegration.tsx';
export type FullPageEditorSideEffectsProps = {
	// Content identification
	contentId: string;
	contentType: string;
	isEmbeddedConfluence: boolean;
	isLivePage: boolean;
	isUnpublishedDraft: boolean;
	isEditorEmpty: boolean;

	// Title state
	title: string | null;
	draftTitle: string | null;
	isAIEnabledForContent: boolean;
	handleTitleChange: (title: string) => void;

	// Connectivity state
	collabEditInitialized: boolean;
	collabEditInternetLost: boolean;
	internetDisconnected: boolean;
	extractedCollabEditProvider: (CollabEditProvider & Provider) | null;

	// Editor APIs
	fullPageEditorPresetAPI: FullPageEditorPresetAPI | undefined;
	setReadPageContent?: (readPageContent: ReadPageContent) => () => void;

	// Comments-related
	isDocumentActive: boolean;
	focusedCommentId: string | undefined;
	replyToCommentId: string | undefined;
	handleUseFocusedCommentGQLError: (error: Error) => void;
	annotationStateRef: React.MutableRefObject<InlineCommentPluginState | undefined>;
	addCommentInCache: (props: AddCommentInCacheProps) => void;
	deleteCommentInCache: (props: { commentId: string; onError?: (error: Error) => void }) => boolean;
	updateUnresolvedInlineCommentFn: (annotationsList: string[]) => void;
	triggerAnalyticsEvent: TriggerAnalyticsEventType;

	// UI state
	proseMirrorRenderedData: Record<string, unknown> | null;
	showBlankPageQuickActions: boolean;
	hasSubmittedCwrPrompt: boolean;
	shouldShowCreateWithRovoExperience: boolean;
	isRovoLLMEnabled: boolean;
	isMarkdownMode: boolean;
	onSpaceKeyPressed: () => void;

	// Feature flags and i18n
	flags: FlagsStateContainer;
	intl: IntlShape;

	// Analytics
	disabled: boolean;
	isTitleFocused: boolean | null;
	shouldRenderPreview: boolean;
	collabEditError: ProviderError | null;
	collabLimitReached: boolean;
	heartbeatError: CollabHeartbeatError | undefined;

	// Annotation/Plugin state
	removeCommentQueryParams: () => void;
	setEditorAnnotations: (annotations: string[], contentId: string) => void;
	getSortedMarkerRefs: (markerRefList: string[], pageMode?: 'view' | 'edit') => string[];
	setOrderedActiveAnnotationIdList: (annotations: string[]) => void;
	editorAnnotationsRef: React.MutableRefObject<string[]>;
};

/**
 * FullPageEditorSideEffects component isolates all side-effect hooks to prevent
 * unnecessary re-renders of the main editor component and its children.
 *
 * By extracting these hooks into a separate component, the main FullPageEditorComponent
 * can re-render without causing all its children to re-render as well.
 *
 * This component is rendered by ConditionalSideEffectsComponent.
 */
export const FullPageEditorSideEffects: React.FC<FullPageEditorSideEffectsProps> = ({
	contentId,
	contentType,
	isEmbeddedConfluence,
	isLivePage,
	isUnpublishedDraft,
	isEditorEmpty,
	title,
	draftTitle,
	isAIEnabledForContent,
	handleTitleChange,
	collabEditInitialized,
	collabEditInternetLost,
	internetDisconnected,
	extractedCollabEditProvider,
	fullPageEditorPresetAPI,
	setReadPageContent,
	isDocumentActive,
	focusedCommentId,
	replyToCommentId,
	handleUseFocusedCommentGQLError,
	annotationStateRef,
	addCommentInCache,
	deleteCommentInCache,
	updateUnresolvedInlineCommentFn,
	triggerAnalyticsEvent,
	proseMirrorRenderedData,
	showBlankPageQuickActions,
	hasSubmittedCwrPrompt,
	shouldShowCreateWithRovoExperience,
	isRovoLLMEnabled,
	isMarkdownMode,
	onSpaceKeyPressed,
	flags,
	intl,
	disabled,
	isTitleFocused,
	shouldRenderPreview,
	collabEditError,
	collabLimitReached,
	heartbeatError,
	removeCommentQueryParams,
	setEditorAnnotations,
	getSortedMarkerRefs,
	setOrderedActiveAnnotationIdList,
	editorAnnotationsRef,
}) => {
	// Get locally-scoped actions from their respective contexts/stores
	const { hideObjectSidebar } = useObjectSidebarActions();
	const { resetInitialDataLoadedMap } = useCommentsPanelActions();
	const { clearCommentsData } = useCommentsDataActions();
	const { clearProcessedData } = useProcessedCommentsDataActions();
	const { clearMentionsFlags } = useMentionsNotificationsActions();
	const { setActiveHighlight } = useInlineCommentsDispatchContext();

	// Side effect: Register this editor as the native-embed read-content provider so object remix can
	// read the open page. Shared with the embedded classic editor; the hook owns register/unregister.
	useRegisterEditorReadPageContent({
		contentId,
		editorPresetAPI: fullPageEditorPresetAPI,
		setReadPageContent,
	});

	// Side effect: Notify the embedding parent product about live-doc text selection context
	useEmbeddedConfluenceTextSelectionBridge({
		contentId,
		enabled: isLivePage,
		fullPageEditorPresetAPI,
		isEmbeddedConfluence,
		sourceTitle: title ?? draftTitle,
	});

	// Side effect: Space key shortcut override for CWR
	// Cast preset API to EditorInjectionAPI — useSharedPluginStateSelector expects injection-shaped APIs.
	const isTypeAheadOpen: boolean =
		useSharedPluginStateSelector(
			fullPageEditorPresetAPI as EditorInjectionAPI<any, any>,
			'typeAhead.isOpen',
		) ?? false;

	const isInlineAIOpen: boolean =
		useSharedPluginStateSelector(
			fullPageEditorPresetAPI as EditorInjectionAPI<any, any>,
			'aiExperience.aiDecorationExperiencePluginState.isRunning',
		) ?? false;

	useSpaceKeyShortcutOverride({
		isActive:
			showBlankPageQuickActions &&
			!hasSubmittedCwrPrompt &&
			!shouldShowCreateWithRovoExperience &&
			!isMarkdownMode &&
			!isInlineAIOpen,
		isTypeAheadOpen,
		isRovoLLMEnabled,
		onSpaceKeyPressed,
		isLivePage,
		editorAPI: fullPageEditorPresetAPI as EditorInjectionAPI<any, any> | undefined,
	});

	// Side effect: Expose Google Meet screen share
	useExposeGoogleMeetScreenShare();

	// Side effect: Scroll on URL change
	useScrollOnUrlChange(proseMirrorRenderedData !== null, undefined, fullPageEditorPresetAPI);

	// Side effect: Print cover injector
	usePrintCoverInjector(expValEquals('afm-enable-command-p-reroute', 'isEnabled', true), intl);

	// Side effect: Focused comment handling
	useFocusedComment({
		commentId: focusedCommentId || replyToCommentId,
		contentId,
		handleGqlError: handleUseFocusedCommentGQLError,
		annotationState: annotationStateRef.current,
	});

	const { createAnalyticsEvent } = useAnalyticsEvents();

	// Side effect: Inline comments PubSub
	const { reloadMissedComments } = useEditorInlineCommentsPubSub({
		contentId,
		contentType,
		triggerAnalyticsEvent,
		updateUnresolvedInlineComment: updateUnresolvedInlineCommentFn,
		isDocumentActive,
		addCommentInCache,
		deleteCommentInCache,
	});

	const handleMissedComments = useCallback(async () => {
		await reloadMissedComments();

		createAnalyticsEvent({
			type: 'sendTrackEvent',
			data: {
				action: 'refetched',
				actionSubject: 'comments',
				objectId: contentId,
				contentType,
				source: 'editPageScreen',
			},
		}).fire();
	}, [reloadMissedComments, createAnalyticsEvent, contentId, contentType]);

	const handleMissedCommentsRef = useRef(handleMissedComments);
	useEffect(() => {
		handleMissedCommentsRef.current = handleMissedComments;
	}, [handleMissedComments]);

	// Side effect: recover comments missed while pub/sub was torn down. Quick reload owns the
	// detection; the payload is unused because the reload derives what changed from the server.
	useQuickReloadSubscriber(
		useMemo(
			() => ({
				name: 'editorMissedComments',
				types: [ReloadType.missedComments],
				priority: ReloadPriority.immediate,
				reload: async () => {
					if (!expValEquals('collab_check_sleep_detection_experiment', 'isEnabled', true)) {
						return;
					}
					await handleMissedCommentsRef.current();
				},
			}),
			[],
		),
	);

	// Side effect: Refresh AI suggestions when AGG publishes
	// `confluence_onSuggestedEditsUpdated` for the active document.
	const onSuggestedEditsUpdated = useCallback(() => {
		fullPageEditorPresetAPI?.aiSuggestions?.actions.refreshSuggestions({
			regenerationTrigger: AiSuggestionsRegenerationTriggerValue.SUGGESTIONS_UPDATED,
		});
	}, [fullPageEditorPresetAPI]);

	useSuggestedEditsUpdatedSubscription({
		contentId,
		contentType: contentType === 'blogpost' ? 'blogpost' : 'page',
		onSuggestedEditsUpdated,
		skip: !(
			isRovoLLMEnabled &&
			expValEquals('platform_editor_ai_content_suggested_edits', 'isEnabled', true)
		),
	});

	// Side effect: Unread comments setup
	useUnreadCommentsSetup({
		pageId: contentId,
		pageContentTypeProps: { pageMode: 'edit' },
	});

	// Side effect: Collab edit connectivity sync
	useEditorCollabConnectivitySync({
		collabEditInternetLost,
		internetDisconnected,
		editorFullPagePresetAPI: fullPageEditorPresetAPI,
	});

	// Side effect: Native collab connecting state
	useConnecting(extractedCollabEditProvider);

	// Side effect: Auto-open Rovo chat mini-modal for new content or empty live docs.
	// Draft/new pages wait for edit-page/load; live docs use collab readiness plus empty editor state.
	useOpenRovoOnEditPageReady({
		contentId,
		isEnabled: expVal('rovo_button_mau_maxxed_minimodal_default', 'isEnabled', false),
		isEmptyLivePage: isLivePage && isEditorEmpty,
		shouldObserveLiveEmptyPage: isLivePage,
		isLivePageReady: isLivePage && collabEditInitialized,
	});

	// Side effect: Resume Content Review from a one-time Suggestions panel deep link.
	useShowSuggestionsPanelFromDeepLink();

	// Side effect: Full page auto focus
	useFullPageAutoFocus(isLivePage);

	// Side effect: AI replacing title
	useEditorAIReplacingTitle({
		enabled: isAIEnabledForContent,
		title: title || draftTitle || '',
		handleNewTitle: handleTitleChange,
		canFireUnifiedAIAnalyticsEvent: true,
	});

	// Side effect: Send content age analytics
	useSendContentAgeAnalytics({ contentId, collabService: 'ncs' });

	// Side effect: Disabled analytics
	useDisabledAnalytics({
		contentId,
		disabled: isTitleFocused || disabled,
		shouldRenderPreview,
		isTitleFocused,
		collabEditError,
		collabLimitReached,
		heartbeatError,
	});

	// Side effect: Schedule publish flags watcher
	useSchedulePublishFlagsWatcher({
		contentId,
		contentType,
		isUnpublishedDraft,
		title: title ?? draftTitle,
		flags,
		isSchedulePublishEnabled: !isLivePage,
	});

	// Side effect: Full page editor loom insertion
	useFullPageEditorLoomInsertion(fullPageEditorPresetAPI);

	// Side effect: Agent run-state bridge — reads live agent run-state from the agent-run-state
	// store and pushes it into the mentions plugin to animate agent mentions
	useAgentRunStateBridge(fullPageEditorPresetAPI);

	// Side effect: Agent mention bridge — reads agent mention plugin state and writes to
	// EditorRovoButtonActionsStore, resolving AAID → Agent Studio UUID via the assistance API.
	useAgentMentionBridge(fullPageEditorPresetAPI);

	// Side effect: Plugin state effect for annotations
	usePluginStateEffect(fullPageEditorPresetAPI as any, ['annotation'], ({ annotationState }) => {
		const annotations = annotationState?.annotations;
		const { selectedAnnotations, mouseData } = annotationState || {};
		annotationStateRef.current = annotationState;

		const handleCommentQueryParams = () => {
			if (
				selectedAnnotations &&
				selectedAnnotations.length > 0 &&
				mouseData?.isSelecting &&
				(focusedCommentId || replyToCommentId)
			) {
				removeCommentQueryParams();
			}
		};

		const setAnnotations = () => {
			const editorAnnotationsToSet = annotations ? Object.keys(annotations) : [];

			setEditorAnnotations(getSortedMarkerRefs(editorAnnotationsToSet, 'edit'), contentId);
			editorAnnotationsRef.current = editorAnnotationsToSet;
			const activeEditorAnnotationsToSet = annotations
				? Object.keys(annotations).filter((key) => annotations[key] === false)
				: [];
			setOrderedActiveAnnotationIdList(getSortedMarkerRefs(activeEditorAnnotationsToSet, 'edit'));
		};

		handleCommentQueryParams();
		setAnnotations();
	});

	// Side effect: Hide object sidebar on content change
	useEffect(() => {
		void hideObjectSidebar();
	}, [contentId, hideObjectSidebar]);

	// Side effect: Comments cleanup on unmount or content change when side effects are isolated
	useEffect(() => {
		return () => {
			resetInitialDataLoadedMap();
			clearCommentsData();
			clearProcessedData();
			clearMentionsFlags();
			setActiveHighlight(null);
			if (UNSAFE_expValEqualsNoExposure('cc_comments_media_viewer_sidebar', 'isEnabled', true)) {
				resetMediaViewerSidebar();
			}
		};
	}, [
		contentId,
		resetInitialDataLoadedMap,
		clearCommentsData,
		clearProcessedData,
		clearMentionsFlags,
		setActiveHighlight,
	]);

	return (
		<>
			{/* Conditionally wire Live Docs editor changes into the Flag-to-Share criteria hook. */}
			{isLivePage && fg('twcg-2055-confluence-onboarding-flag-to-share-fg') && (
				<LiveDocsFlagToShareSideEffect contentId={contentId} editorApi={fullPageEditorPresetAPI} />
			)}
		</>
	);
};
